import { APP_BASE_HREF, PlatformLocation } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Apollo, gql } from 'apollo-angular';
import { AppConfigurationFeature, Calendar, CalendarEvent, CalendarEventCategory, CalendarEventTemplate, CalendarRegistrationForm, CoreLibService, Direction, FsFile, Group, Location, MediaReference, MediaReferenceInput, MemberListVisibility, ModificationScope, Mutation, ProductDefinition, ProductType, Query, Resource, UserProfile } from 'core-lib';
import { GroupPickerComponent, MediaLibraryLibComponent, LocationEditComponent } from 'core-ui';
import { FilestoreBrowserComponent } from 'filestore-lib';
import { PaymentLibService } from 'payment-lib';
import {ConfirmationService, MessageService, ToastMessageOptions} from 'primeng/api';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Options, RRule } from 'rrule';
import { Observable, forkJoin } from 'rxjs';
import  moment from 'moment';
import {ActivatedRoute} from "@angular/router";

@Component({
  selector: 'lib-appointment-management-edit',
  templateUrl: './appointment-management-edit.component.html',
  styleUrls: ['./appointment-management-edit.component.scss'],
  providers: [{ provide: APP_BASE_HREF, useFactory: (s: PlatformLocation) => s.getBaseHrefFromDOM(), deps: [PlatformLocation] }],
  standalone: false
})

export class AppointmentManagementEditComponent implements OnInit {

  moment = moment;
  FORM_CYCLE_OPTIONS = [
    { id: 'ONCE', label: $localize`:@@appointment-management-edit.cycle.once: Once` },
    { id: 'DAYS', label: $localize`:@@appointment-management-edit.cycle.days: Days` },
    { id: 'WEEKS', label: $localize`:@@appointment-management-edit.cycle.weeks: Weeks` },
    { id: 'MONTHS', label: $localize`:@@appointment-management-edit.cycle.months: Months` },
    { id: 'YEARS', label: $localize`:@@appointment-management-edit.cycle.years: Years` },
    { id: 'CUSTOM', label: $localize`:@@appointment-management-edit.cycle.custom: Custom` },
  ];


  MEMBER_LIST_VISIBILITIES = [
    { id: MemberListVisibility.Admin, label: $localize`:@@appointment-management-edit.memberlist.visibility.Admin:Appointment manager` },
    { id: MemberListVisibility.Members, label: $localize`:@@appointment-management-edit.memberlist.visibility.Members:Other participants` },
    { id: MemberListVisibility.Profile, label: $localize`:@@appointment-management-edit.memberlist.visibility.Profile:Every user with profile` }
  ];

  contactProfilesSearch: UserProfile[] = [];
  contactProfile: UserProfile;
  parentAppointment: CalendarEvent;
  appointmentForm: UntypedFormGroup;
  products: ProductDefinition[] = [];
  allLocations: Location[] = [];
  imgList: MediaReference[] | Resource[];
  currentPagedImageIndex = 0;
  myUserProfile: UserProfile;
  tinyMceConfig: any;
    editorLoaded: boolean = false;

  registrationForms: CalendarRegistrationForm[] = [];
  calendars: Calendar[] = [];
  categories: CalendarEventCategory[] = [];
  dialogControl = { context: null, showAppointmentSeriesEditConfirmDialog: false, showAppointmentSeriesDeleteConfirmDialog: false, showImageSourceDialog: false };
  selectedGroup: Group;
  selectedGroupRegistration: Group;
  selectedGroupInvitation: Group;
  loadEditor: boolean = false;
  productsEnabled = false;
  appointmentManagerGroupDisabled = false;
  firstLoad: boolean = true;
  hiddenElements = [];

  CREATE_EVENT_MUTATION = gql`
  mutation createCalendarEvents( $calendarId : ID! ,$data: CalendarEventTemplate!){
    createCalendarEvents(calendarId : $calendarId, data : $data) {
      id
      calendarId
      seriesId
      title
      subTitle
      description
      dateStart
      dateTimeStart
      dateEnd
      dateTimeEnd
      maxSignUps
      maxAdditionalSignUps
      multiSignUpAllowed
      onlineConference
      paymentDocumentation
      registrationFormId
      products{
        id
        name
        image {
          url
        }
      }
      iconImage{id url}
      resources{id url}
      deadlineStartOffsetMinutes
      startOfRegistrationOffsetMinutes
      cancelRegistrationDeadlineOffsetMinutes
      notifyEventContactPersonOnSignUp
      issueTicketsUponRegistration
      memberListVisibility
      manageAttendees
      publicRegistrationAllowed
      waitingListEnabled
      maxReservationTime
      badgeEnabled
      contactId
      contactGroup{
        id
        name
        numberOfMembers
      }
      contactGroupId
      contact{
        id
        realname
      }
      canWrite
      reminder{
        active
        message
        value
      }
      feedbackAllowed
    }
  }`;

  DELETE_EVENT_MUTATION = gql`
    mutation deleteCalendarEvent($id: ID!, $scope: ModificationScope!){
      deleteCalendarEvent(id:$id,scope: $scope)
  }`;

  LOAD_LOCATIONS_QUERY = gql`
  query listLocations{
    listLocations {
      id
      appId
      postalCode
      street
      city
      title
      geoLat
      geoLng
  }}`;

  UPDATE_CALENDAR_EVENT = gql`
    mutation updateCalendarEvents($eventId: ID!, $data : CalendarEventTemplate!, $seriesId: ID,$scope: ModificationScope!){
      updateCalendarEvents(eventId: $eventId, data : $data, seriesId: $seriesId, scope: $scope){
        id
        calendarId
        seriesId
        title
        subTitle
        description
        dateStart
        dateTimeStart
        dateEnd
        dateTimeEnd
        maxSignUps
        maxAdditionalSignUps
        multiSignUpAllowed
        iconImage{id}
        resources{id}
        onlineConference
        paymentDocumentation
        registrationFormId
        products{
          id
          name
          image {
            url
          }
        }
        deadlineStartOffsetMinutes
        startOfRegistrationOffsetMinutes
        cancelRegistrationDeadlineOffsetMinutes
        notifyEventContactPersonOnSignUp
        issueTicketsUponRegistration
        memberListVisibility
        manageAttendees
        publicRegistrationAllowed
        waitingListEnabled
        maxReservationTime
        badgeEnabled
        contactId
        contact{
          id
          realname
        }
        reminder{
          active
          message
          value
        }
        feedbackAllowed
  }}`;


  LOAD_CALENDARS = gql`
  query combinedInitQuery($componentId:ID!){
    getCalendarRegistrationForms{
      name
      id
      usedInEventsCount
    }
    listCalendarByComponentId(componentId: $componentId) {
      id
      title
      color
      canWrite
      canRead
      readerRoles{
        value
      }
      writerRoles{
        value
      }
      categories{
        id
        title
        color
      }
    }
  }`;

  QUERY_GET_CURRENT_EVENT = gql`
  query getCalendarEventById($calendarEventId: ID!){
    getCalendarEventById(calendarEventId: $calendarEventId) {
      contactGroup{
        id
        name
        numberOfMembers
      }
  }}`;

  constructor(public ref: DynamicDialogRef, public config: DynamicDialogConfig, private apolloClient: Apollo, public coreLibService: CoreLibService,
              private dialogService: DialogService, private messageService: MessageService, @Inject('component') private component: string, @Inject('language') private language: string,
              private confirmationService: ConfirmationService, @Inject(APP_BASE_HREF) public baseHref: string, private paymentService: PaymentLibService, private route: ActivatedRoute) {
    this.parentAppointment = config.data.appointment;
  }

  ngOnInit(): void {
    let start = moment();
    let hour = start.hour();
    let minute = start.minute();
    if (minute <= 30) start.set({ minute: 30 });
    if (minute > 30) start.set({ hour: hour + 1, minute: 0 });

    if (this.config.data.startDate) {
      start = moment(this.config.data.startDate);
      start.set({ hour: hour + 1, minute: 0 })
    }
    let end = start.clone();
    end = end.add(1, 'hour');

      this.coreLibService.hook('hook_appointment_management_edit_groupmanager_disabled', { component: this.component}, false).then((value) => {
      this.appointmentManagerGroupDisabled = value;
    });

      this.configureTinyMce();

    this.appointmentForm = new UntypedFormGroup({
      "title": new UntypedFormControl(null, [Validators.required]),
      "subTitle": new UntypedFormControl(null),
      "calendarId": new UntypedFormControl(null, [Validators.required]),
      "description": new UntypedFormControl(null),
      "locationId": new UntypedFormControl(null),
      "categoryIds": new UntypedFormControl(null),
      "contactGroupId": new UntypedFormControl(null),
      "allDay": new UntypedFormControl(false),
      "onlineConference": new UntypedFormControl(false),
      "paymentDocumentation": new UntypedFormControl(false),
      "registrationFormId": new UntypedFormControl(null),
      "productIds": new UntypedFormControl([]),
      "dateStart": new UntypedFormControl(start.format('YYYY-MM-DD'), Validators.required),
      "timeStart": new UntypedFormControl(start.format('HH:mm')),
      "dateEnd": new UntypedFormControl(end.format('YYYY-MM-DD'), [Validators.required]),
      "timeEnd": new UntypedFormControl(end.format('HH:mm')),
      "cycle": new UntypedFormControl(this.FORM_CYCLE_OPTIONS[0].id),
      "rrule": new UntypedFormControl(null, [this.rruleValidator()]),
      "appointmentInterval": new UntypedFormControl({ disabled: false, value: 1 }),
      "appointmentEndType": new UntypedFormControl("endDate"),
      "appointmentEndAtDate": new UntypedFormControl({ disabled: false, value: null }),
      "appointmentCount": new UntypedFormControl({ disabled: false, value: 1 }),
      "appointmentWeekday": new UntypedFormControl([]),
      "iconImage": new UntypedFormControl(null),
      "resources": new UntypedFormControl(null),
      "registrationRequired": new UntypedFormControl(false),
      "deadlineStartOffsetMinutes": new UntypedFormControl(null),
      "deadline": new UntypedFormControl(null),
      "startOfRegistrationOffsetMinutes": new UntypedFormControl(null),
      "startOfRegistration": new UntypedFormControl(null),
      "cancelRegistrationDeadlineOffsetMinutes": new UntypedFormControl(null),
      "cancelRegistrationDeadline": new UntypedFormControl(null),
      "notifyEventContactPersonOnSignUp": new UntypedFormControl(true),
      "maxAdditionalSignUps": new UntypedFormControl({ value: 0, disabled: false }, []),
      "availableAppointmentSlots": new UntypedFormControl({ value: null, disabled: false }, []),
      "multiSignUpAllowed": new UntypedFormControl(false),
      "minimumEventSignUps": new UntypedFormControl(null),
      "contactId": new UntypedFormControl(),
      "detailLink": new UntypedFormControl(null, [Validators.pattern('(https?://).*')]),
      "issueTicketsUponRegistration": new UntypedFormControl({ value: false, disabled: false }, []),
      "memberListVisibility": new UntypedFormControl(MemberListVisibility.Admin),
      "manageAttendees": new UntypedFormControl(null),
      "publicRegistrationAllowed": new UntypedFormControl(false),
      "groupRegistration": new UntypedFormControl(null),
      "groupInvitation": new UntypedFormControl(null),
      "reminder": new UntypedFormGroup({
        "active": new UntypedFormControl({ value: false, disabled: false }),
        "message": new UntypedFormControl({ value: null, disabled: false }),
        "value": new UntypedFormControl({ value: 2, disabled: false })
      }),
      "waitingListEnabled": new UntypedFormControl(null),
      "maxReservationTime": new UntypedFormControl(60),
      "badgeEnabled": new UntypedFormControl(true),
      "feedbackAllowed": new UntypedFormControl(false)
    });
    if(this.coreLibService.getSecurityInformation().userProfile){
      this.appointmentForm.get("contactId").patchValue(this.coreLibService.getSecurityInformation().userProfile.id)
    }
    this.loadLocations();

    const baseQueries = [this.apolloClient.query<Query>({
      query: this.LOAD_CALENDARS,
      variables: {
        componentId: this.component
      }
    })];
    if (this.parentAppointment && this.parentAppointment.id) {
      baseQueries.push(this.apolloClient.query<Query>({
        query: this.QUERY_GET_CURRENT_EVENT,
        variables: {
          calendarEventId: this.parentAppointment.id
        }
      }));
    }

    forkJoin(baseQueries).subscribe(results => {
      const calendarResult = results[0];
      if (results.length > 1) {
        const eventResult = results[1];
        this.parentAppointment.contactGroup = eventResult.data.getCalendarEventById.contactGroup;
        if (this.parentAppointment && this.parentAppointment.contactGroup) {
          this.selectedGroup = this.parentAppointment.contactGroup;
        }
      }
      this.registrationForms = calendarResult.data.getCalendarRegistrationForms;

      let calendarParams: string[] = this.route.snapshot.queryParams.calendarIds?.split(',');
      this.calendars = calendarResult.data.listCalendarByComponentId.filter(c => {
          // wenn über queryparameter gefiltert wird sollen nur diese Kalender hier auswählbar sein
        if(calendarParams && calendarParams.length && !calendarParams.includes(c.id)){
            return false;
        }
        if (c.canWrite) {
          return true;
        }
        if (this.parentAppointment && this.parentAppointment.calendarId === c.id) {
          return true;
        }
        return false;
      });

      if (this.parentAppointment) {
        this.initFormFromExistingEvent(this.parentAppointment);
      } else {
        /**
         * Preselect calendarId
         */
        let preselectedCalendarId: string = null;
        if (this.config.data.preselectedCalendarId && this.calendars.findIndex(c=>c.id === this.config.data.preselectedCalendarId)>-1) {
          preselectedCalendarId = this.config.data.preselectedCalendarId;
          this.appointmentForm.patchValue({calendarId:preselectedCalendarId});
          this.onCalendarChanged({value: preselectedCalendarId});
        }
      }
    });

    this.coreLibService.getAppConfiguration().subscribe((config) => {
      if (config.features.indexOf(AppConfigurationFeature.ProductCatalogue) > -1) {
        this.productsEnabled = true;
        this.paymentService.listProductsByTypes([ProductType.MultiTicket, ProductType.SingleTicket, ProductType.SeasonTicket], true).subscribe((result) => {
          this.products = result.filter(pd => pd.paymentProviderReference != null);
        });
      }
    });

    this.myUserProfile = this.coreLibService.getSecurityInformation().userProfile;
  }

  onSelectImages(target: string): void {
    const hasPermissionForLibrary = this.coreLibService.hasAnyClaims(['resource_create']);
    if (!hasPermissionForLibrary) {
      this.onSelectImagesFromFileStore(target);
    } else {
      this.dialogControl.context = target;
      this.dialogControl.showImageSourceDialog = true;
    }
  }

  onSelectImagesFromMediaLibrary(target: string): void {
    this.dialogControl.showImageSourceDialog = false;

    const dialog = this.dialogService.open(MediaLibraryLibComponent, {
      header: $localize`:@@media-library.header.title: Media Library`,
      data: {
        filterOptions: ['image'],
        selectionMode: target === 'icon' ? 'single' : 'multi'
      },
      width: '90%',
      height: '90%',
      modal: true,
      draggable: false,
      closable: true,
      focusOnShow: false,
      closeAriaLabel:$localize`:@@media-library.header.closeButton:Close`,
      focusTrap: true,
      contentStyle: { display: 'flex' }
    });

    dialog.onClose.subscribe((result) => {
      if (target === 'icon') {
        if (result && result.resources) {
          const r = result.resources as Resource;
          this.appointmentForm.get('iconImage').setValue(r);
          this.appointmentForm.patchValue({ 'iconImage': r });

        }
      } else if (target === 'resources') {
        const r = result.resources as [Resource];
        this.currentPagedImageIndex = 0;
        this.imgList = r;
        this.appointmentForm.get('resources').setValue(r);
      }
    });
  }

  onSelectImagesFromFileStore(target: string): void {
    this.dialogControl.showImageSourceDialog = false;


    import('filestore-lib').then(() => {
      const dialog = this.dialogService.open(FilestoreBrowserComponent, {
        header: $localize`:@@filestore-lib.personal-store:Personal file storage`,
        data: {
          ownerId: this.myUserProfile.id,
          options: {
            limit: target === 'icon' ? 1 : 5,
            mimeTypes: ['image']
          }
        },
        width: '90%',
        height: '90%',
        modal: true,
        draggable: false,
        closable: true,
        focusOnShow: false,
        closeAriaLabel:$localize`:@@filestore-lib.personal-store.closeButton:Close`,
        focusTrap: true,
        contentStyle: { display: 'flex' }
      });


      dialog.onClose.subscribe((result) => {
        if (target === 'icon') {
          const resources = result as [FsFile];
          const mediaReferences = resources.map(r => ({ url: r.thumbnailImageUrl, id: r.id }));
          this.appointmentForm.get('iconImage').setValue(mediaReferences[0]);
          this.appointmentForm.patchValue({ 'iconImage': mediaReferences[0] });
        } else if (target === 'resources') {
          const resources = result as [FsFile];
          const mediaReferences = resources.map(r => ({ url: r.thumbnailImageUrl, id: r.id }));
          this.currentPagedImageIndex = 0;
          this.imgList = mediaReferences;
          this.appointmentForm.get('resources').setValue(mediaReferences);
        }
      });
    });

  }

  /**
   * on date start selected
   * @param event
   */
  onDateStartSelected(value): void {
    const dateEndValue = this.appointmentForm.get('dateEnd').value;
    if (dateEndValue == null || moment(dateEndValue).isBefore(this.appointmentForm.get('dateStart').value)) {
      this.appointmentForm.get('dateEnd').setValue(moment(value).format('YYYY-MM-DD'));
    }

  }

  /**
   * on date start selected
   * @param event
   */
  onDateTimeStartSelected(value): void {
    this.appointmentForm.get('timeEnd').setValue(moment(`01-01-1970 ${value}`).add(1, 'hour').format('HH:mm'));
  }
  /**
   *
   * @param event
   */
  onSelectedCategoriesChanged(event): void {
    this.appointmentForm.get('categoryIds').setValue(event.value.map((cat) => cat.id));
  }
  /**
   * updates category dropdown if calendar selected
   * @param event
   */
  onCalendarChanged(event: any): void {
    const calender = this.calendars.find((c) => c.id === event.value);
    this.categories = calender.categories;
    this.appointmentForm.get('categoryIds').setValue([]);
  }


  deletePreviewImage() {
    this.appointmentForm.get('iconImage').setValue(null);
  }

  deleteContentImages() {
    this.imgList = [];
    this.appointmentForm.get('resources').setValue([]);
  }

  /**
   * Creates RRule String
   * @returns RRule String
   */
  createRRule() {
    var ruleFreq;
    var ruleInterval: number;
    var ruleByWeekday;


    //Determine Cycle
    //Todo: Daily, Weekly usw. noch durch ID's austauschen. Nicht die Values hardcoden!
    switch (this.appointmentForm.controls['cycle'].value) {
      case "DAYS":
        ruleFreq = RRule.DAILY;
        break;
      case "WEEKS":
        ruleFreq = RRule.WEEKLY;
        break;
      case "MONTHS":
        ruleFreq = RRule.MONTHLY;
        break;
      case "YEARS":
        ruleFreq = RRule.YEARLY;
        break;
      default:
        break;
    }

    let ruleDtStart = moment(`${this.appointmentForm.value.dateStart} ${this.appointmentForm.value.timeStart}`, 'YYYY-MM-DD HH:mm').toDate();

    //Set Weekday (Only in Weekly Set)
    if (this.appointmentForm.value.appointmentWeekday != null) {
      ruleByWeekday = this.appointmentForm.value.appointmentWeekday.map(Number);
    }

    ruleInterval = this.appointmentForm.value.appointmentInterval;


    //build Rule object here. Check if various fields have been filled out
    let newRule: Partial<Options> = {
      freq: ruleFreq,
      interval: ruleInterval,
      dtstart: ruleDtStart
    };

    if (ruleFreq == RRule.WEEKLY) {
      newRule["byweekday"] = ruleByWeekday;
    }


    if (this.appointmentForm.value.cycle === 'CUSTOM') {
      newRule = RRule.parseString(this.appointmentForm.value.rrule)
    }

    if (this.appointmentForm.value.appointmentEndType === 'endDate') {
      newRule["until"] = moment(this.appointmentForm.value.appointmentEndAtDate).endOf('day').toDate();
    } else if (this.appointmentForm.value.appointmentEndType === 'repeats') {
      newRule["count"] = this.appointmentForm.value.appointmentCount;
    }


    //Regex removes unwanted String at the beginning of the rule string -> Blame the used lib for that one
    var rule = new RRule(newRule);
    //return rule.toString().replace(new RegExp("([^x]+)RRULE:"), "");
    // return rule.toString().replace(new RegExp("RRULE:"), "");
    return rule.toString().substring(rule.toString().indexOf('RRULE') + 6);
  }

  /**
   * rrule validator
   * @returns
   */
  rruleValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value == null) {
        return null;
      }

      try {
        RRule.parseString(value)
        return null;
      } catch (e) {
        return { rrule: e };
      }
    }
  }
  /**
   * Create appointment create or update data
   */
  createEventData() {
    let form = this.appointmentForm.value;

    const now = moment()
    const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const utcOffSet = now.format('Z');

    let data = {
      title: form.title,
      calendarId: form.calendarId,
      detailLink: form.detailLink,
      subTitle: form.subTitle,
      onlineConference: form.onlineConference,
      description: form.description,
      paymentDocumentation: form.paymentDocumentation,
      registrationFormId: form.registrationFormId,
      productIds: form.productIds,
      categoryIds: form.categoryIds,
      locationId: form.locationId,
      contactGroupId: form.contactGroupId,
      allDay: form.allDay,
      multiSignUpAllowed: form.multiSignUpAllowed,
      registrationRequired: form.registrationRequired,
      maxSignUps: form.availableAppointmentSlots,
      notifyEventContactPersonOnSignUp: form.notifyEventContactPersonOnSignUp,
      contactId: form.contactId,
      issueTicketsUponRegistration: form.issueTicketsUponRegistration,
      memberListVisibility: form.memberListVisibility,
      manageAttendees: form.manageAttendees,
      publicRegistrationAllowed: form.publicRegistrationAllowed,
      deadlineStartOffsetMinutes: form.deadlineStartOffsetMinutes,
      startOfRegistrationOffsetMinutes: form.startOfRegistrationOffsetMinutes,
      cancelRegistrationDeadlineOffsetMinutes: form.cancelRegistrationDeadlineOffsetMinutes,
      reminder: form.reminder,
      waitingListEnabled: form.waitingListEnabled,
      maxReservationTime: form.maxReservationTime,
      badgeEnabled: form.badgeEnabled,
      timeZoneId: timeZoneId,
      timeZoneOffset: utcOffSet,
      feedbackAllowed: form.feedbackAllowed,
      groupInvitation: form.groupInvitation,
      sourceApplication: this.component

    } as CalendarEventTemplate;


    if (this.parentAppointment == null || this.config.data.clonedAppointment == true) {
      if (form['cycle'] !== 'ONCE') {
        data["rrule"] = this.createRRule();
      }
    }


    if (this.parentAppointment == null && form.groupRegistration) {
      data["groupRegistration"] = form.groupRegistration;
    }

    if (form.allDay != false && form.allDay != null) {
      data["dateStart"] = moment(`${form.dateStart}`, 'YYYY-MM-DD').startOf('day').toDate();
      data["dateEnd"] = moment(`${form.dateEnd}`, 'YYYY-MM-DD').endOf('day').toDate();
    } else {
      let startDate = moment(`${form.dateStart} ${form.timeStart}`, 'YYYY-MM-DD HH:mm').toDate();
      let endDate = moment(`${form.dateEnd} ${form.timeEnd}`, 'YYYY-MM-DD HH:mm').toDate();
      data["dateStart"] = startDate
      data["dateEnd"] = endDate;
    }

    if (moment(data["dateEnd"]).isBefore(moment(data["dateStart"]))) {
      throw "INVALID_DATE"
    }

    if (form.iconImage != null) {
      data["iconImage"] = { url: form.iconImage.url, id: form.iconImage.id, mimeType: form.iconImage.mimeType } as MediaReferenceInput
    } else {
      data["iconImage"] = null;
    }

    if (form.resources != null) {
      data["resources"] = form.resources.map(a => { return { url: a.url, id: a.id, mimeType: a.mimeType } });
    } else {
      data["resources"] = [];
    }

    if (form.registrationRequired == true) {
      data["maxAdditionalSignUps"] = form.maxAdditionalSignUps;
      data["multiSignUpAllowed"] = form.maxAdditionalSignUps > 0;

      if (form.minimumEventSignUps > 0) {
        data["minSignUps"] = form.minimumEventSignUps;
      } else if (form.minimumEventSignUps == 0 || form.minimumEventSignUps == null) {
        data["minSignUps"] = null;
      }

    }
    return data;
  }

  applyUpdateChanges(seriesId: string, modificationScope: ModificationScope) {
    let eventId = this.config.data.appointment.id;
    let eventData = {};
    try {
      eventData = this.createEventData();
    } catch (e) {
      if (e === 'INVALID_DATE') {
        return;
      }
    }

    this.apolloClient.mutate<Mutation>({
      mutation: this.UPDATE_CALENDAR_EVENT,
      variables: {
        eventId: eventId,
        seriesId: seriesId,
        data: eventData,
        scope: modificationScope
      }
    }).subscribe((result) => {
      this.ref.close(result);
    });
  }

  /**
   *
   */
  validateBeforeSubmit(): string | null {
    if (this.appointmentForm.get('cycle').value === 'WEEKS' && this.appointmentForm.get('appointmentWeekday').value.length == 0) {
      return $localize`:@@appointment-management-edit.validation.cycle.weeks: Please select at least one day of the week.`;
    }
    if (this.appointmentForm.get('cycle').value !== 'ONCE' && this.appointmentForm.get('appointmentEndType').value == 'endDate' && this.appointmentForm.get('appointmentEndAtDate').value == null) {
      return $localize`:@@appointment-management-edit.validation.appointmentEndType.appointmentEndAtDate: Please add an end date.`;
    }
    if (this.appointmentForm.get('cycle').value !== 'ONCE' && this.appointmentForm.get('appointmentEndType').value == 'repeats' && this.appointmentForm.get('appointmentCount').value == null) {
      return $localize`:@@appointment-management-edit.validation.appointmentEndType.repeats: Please add a number of repeats.`;
    }
    if (this.appointmentForm.get('cycle').value === 'CUSTOM' && (this.appointmentForm.get('rrule').value == null || this.appointmentForm.get('rrule').value.length == 0)) {
      return $localize`:@@appointment-management-edit.validation.appointmentEndType.custom: Please add a valid RRule for custom repeats.`;
    }

    if (this.appointmentForm.get('timeEnd').value === "" && this.appointmentForm.get('allDay').value === false) {
      return $localize`:@@appointment-management-edit.validation.appointmentEndType.endTimeNotSet: Please add an end date.`;
    }

    if (this.appointmentForm.get('reminder').get('active').value === true && this.appointmentForm.get('reminder').get('message').value === "") {
      return $localize`:@@appointment-management-edit.validation.reminder.messageEmpty:Please enter a message if you use the reminder function.`;
    }

    if (this.appointmentForm.get('waitingListEnabled').value === true && this.appointmentForm.get('maxAdditionalSignUps').value > 0) {
      return $localize`:@@appointment-management-edit.validation.waitingListEnabled-conflict:The waiting list function cannot be used in conjunction with additional registrations.`;
    }

    const startOfRegistrationOffsetMinutes = this.appointmentForm.get('startOfRegistrationOffsetMinutes').value;
    if (startOfRegistrationOffsetMinutes && startOfRegistrationOffsetMinutes < 0) {
      return $localize`:@@appointment-management-edit.validation.startOfRegistrationOffsetMinutes.invalid:Registration must start before the appointment.`;
    }

    const cancelRegistrationDeadlineOffsetMinutes = this.appointmentForm.get('cancelRegistrationDeadlineOffsetMinutes').value;
    if (cancelRegistrationDeadlineOffsetMinutes && cancelRegistrationDeadlineOffsetMinutes < 0) {
      return $localize`:@@appointment-management-edit.validation.cancelRegistrationDeadlineOffsetMinutes.invalid:Cancellation deadline must be set before the start of the appointment.`;
    }


    if (this.parentAppointment && this.appointmentForm.value.availableAppointmentSlots != null) {
      let invalidMaxSignUps: Boolean = this.parentAppointment.registrationCount > this.appointmentForm.value.availableAppointmentSlots;
      if (invalidMaxSignUps) {
        return $localize`:@@appointment-management-edit.validation.registrationCount.availableAppointmentSlots: Invalid configuration of the maximum participants. More participants than allowed are already registered for the event.`;
      }
    }

    if (this.appointmentForm.get('contactId').value == null && this.appointmentForm.get('publicRegistrationAllowed').value == true) {
      return $localize`:@@appointment-management-edit.validation.publicRegistration-contact-invalid:To allow public registration, a specific person must be responsible for the appointment. A group cannot be assigned, as the registrations are attributed to the individual.`;
    }
    if (this.appointmentForm.get('productIds').value.length > 0 && this.appointmentForm.get('publicRegistrationAllowed').value == true) {
      return $localize`:@@appointment-management-edit.validation.publicRegistration-product-invalid: Public registrations do not work with paid appointments.`;
    }
    return null;
  }

  /**
   *
   * @param scope
   */
  onSeriesEditConfirmed(scope): void {
    this.applyUpdateChanges(this.parentAppointment.seriesId, scope);
  }
  /**
   * create or update event
   * @returns
   */
  onSubmit() {
    const validated = this.validateBeforeSubmit();
    this.appointmentForm.markAllAsTouched();
    if (this.appointmentForm.valid) {
      if (validated != null) {
        this.messageService.add({ severity: 'error', summary: $localize`:@@appointment-management-edit.validation.error: Invalid configuration.`, detail: validated });
        return;
      }
      if (this.parentAppointment && this.parentAppointment.id != null) {
        this.dialogControl.context = { changeType: ModificationScope.Selected };
        if (this.parentAppointment.seriesId) {
          this.dialogControl.showAppointmentSeriesEditConfirmDialog = true;
        } else {
          this.applyUpdateChanges(null, ModificationScope.Selected);
        }

      } else {
        let eventData = {};
        try {
          eventData = this.createEventData();
        } catch (e) {
          if (e === 'INVALID_DATE') {
            return;
          }
        }
        this.apolloClient.mutate<Mutation>({
          mutation: this.CREATE_EVENT_MUTATION,
          variables: {
            calendarId: this.appointmentForm.get('calendarId').value,
            data: eventData
          }
        }).subscribe((result) => {
          this.ref.close(result);
        });
      }
    }
    else {
      this.scrollToFirstInvalidControl();
    }
  }

  scrollToFirstInvalidControl() {
    let form = document.getElementById('appointmentForm');
    let firstInvalidControl = form.getElementsByClassName('ng-invalid')[0];
    firstInvalidControl.scrollIntoView();
    (firstInvalidControl as HTMLElement).focus();
  }

   hideElementsOnIOS() {
    for (let id of this.hiddenElements) {
      let element = document.getElementById(id);
      if(element != null && !element.getAttribute("aria-hidden")) {
        element?.setAttribute("aria-hidden", "true");
      }
    }
  }

  ngAfterViewChecked(): void {

    if (this.firstLoad) {
      let elem = null;
      if (this.coreLibService.isNativeIOS()) {
        this.hideElementsOnIOS()
        elem = document.getElementById("appointmentForm")?.querySelectorAll("input")[0] as HTMLElement;
      } else {
        elem = document.getElementById("appointmentForm")?.querySelectorAll("label")[0] as HTMLElement;
      } if (elem != null) {
        elem?.setAttribute("tabindex", "0");
        elem?.focus();

        this.firstLoad = false;
      }
    }
  }

  /**
   * on delete event
   */
  onDelete() {
    const seriesId = this.parentAppointment.seriesId;
    if (seriesId) {
      this.dialogControl.context = { changeType: ModificationScope.Selected };
      this.dialogControl.showAppointmentSeriesDeleteConfirmDialog = true;
    } else {
      this.confirmationService.confirm({
        accept: () => {
          this.onDeleteEvent(ModificationScope.Selected);
        },
        rejectButtonStyleClass: 'p-button-secondary',
        acceptButtonStyleClass: 'p-button-danger',
        rejectIcon: 'none',
        acceptIcon: 'none',
        defaultFocus: 'none',
        acceptLabel: $localize`:@@appointment-management-edit.confirm-delete.yes: Yes`,
        rejectLabel: $localize`:@@appointment-management-edit.confirm-delete.cancel: Cancel`,
        message: $localize`:@@appointment-management-edit.confirm-delete: Do you really want to delete the event?`
      });
    }
  }

  /**
   * delete single event
   */
  onDeleteEvent(modificationScope: ModificationScope) {
    let eventId = this.config.data.appointment.id;
    this.apolloClient.mutate<Mutation>({
      mutation: this.DELETE_EVENT_MUTATION,
      variables: { id: eventId, scope: modificationScope }
    }).subscribe((result) => {
      let obj = {
        result: result,
        closeRef: true
      }
      this.ref.close(obj);
    });
  }

  //Handle Locations
  loadLocations() {
    this.apolloClient.query<Query>({
      query: this.LOAD_LOCATIONS_QUERY,
    }).subscribe((result) => {
      this.allLocations = result.data.listLocations;
    });
  }

  /**
   * loads form from existing event
   * @param appointment
   */
  initFormFromExistingEvent(appointment: CalendarEvent) {
    const calender = this.calendars.find((c) => c.id === appointment.calendarId);
    this.categories = calender.categories;

    let categoryIds = [];
    if (appointment.categories) {
      categoryIds = appointment.categories.map(cat => cat.id);
    }
    this.contactProfile = appointment.contact;

    this.appointmentForm.patchValue({
      "title": appointment.title,
      "subTitle": appointment.subTitle,
      "description": appointment.description,
      "detailLink": appointment.detailLink,
      "calendarId": appointment.calendarId,
      "contactGroupId": appointment.contactGroupId,
      "categoryIds": categoryIds,
      "dateStart": moment(appointment.dateStart).format('YYYY-MM-DD'),
      "dateEnd": moment(appointment.dateEnd).format('YYYY-MM-DD'),
      "timeStart": moment(appointment.dateStart).format('HH:mm'),
      "timeEnd": moment(appointment.dateEnd).format('HH:mm'),
      "allDay": appointment.allDay,
      "onlineConference": appointment.onlineConference,
      "paymentDocumentation": appointment.paymentDocumentation,
      "registrationFormId": appointment.registrationFormId,
      "productIds": appointment.products.map(p => p.id),
      "locationId": appointment.location?.id,
      "multiSignUpAllowed": appointment.multiSignUpAllowed,
      "contactId": appointment.contactId,
      "notifyEventContactPersonOnSignUp": appointment.notifyEventContactPersonOnSignUp,
      "issueTicketsUponRegistration": appointment.issueTicketsUponRegistration,
      "memberListVisibility": appointment.memberListVisibility,
      "manageAttendees": appointment.manageAttendees,
      "publicRegistrationAllowed": appointment.publicRegistrationAllowed,
      "maxReservationTime": appointment.maxReservationTime,
      "waitingListEnabled": appointment.waitingListEnabled,
      "badgeEnabled": appointment.badgeEnabled,
      "reminder": appointment.reminder,
      "feedbackAllowed": appointment.feedbackAllowed
    });


    if (appointment.registrationRequired) {
      this.appointmentForm.patchValue({
        "registrationRequired": true,
        "availableAppointmentSlots": appointment.maxSignUps,
        "maxAdditionalSignUps": appointment.maxAdditionalSignUps,
        "minimumEventSignUps": appointment.minSignUps,
        "deadlineStartOffsetMinutes": appointment.deadlineStartOffsetMinutes,
        "startOfRegistrationOffsetMinutes": appointment.startOfRegistrationOffsetMinutes,
        "cancelRegistrationDeadlineOffsetMinutes": appointment.cancelRegistrationDeadlineOffsetMinutes
      });
      if (appointment.deadlineStartOffsetMinutes) {
        this.appointmentForm.patchValue({ "deadline": moment(appointment.dateStart).subtract(appointment.deadlineStartOffsetMinutes, 'minutes').toDate() });
      }

      if (appointment.startOfRegistrationOffsetMinutes) {
        this.appointmentForm.patchValue({ "startOfRegistration": moment(appointment.dateStart).subtract(appointment.startOfRegistrationOffsetMinutes, 'minutes').toDate() });
      }

      if (appointment.cancelRegistrationDeadlineOffsetMinutes) {
        this.appointmentForm.patchValue({ "cancelRegistrationDeadline": moment(appointment.dateStart).subtract(appointment.cancelRegistrationDeadlineOffsetMinutes, 'minutes').toDate() });
      }
    }

    if (appointment.iconImage) {
      this.appointmentForm.patchValue({ 'iconImage': appointment.iconImage });
    }

    if (appointment.resources != null) {
      this.imgList = appointment.resources;
      this.appointmentForm.patchValue({ 'resources': appointment.resources });
    }

  }

  onImagePagingatorChanged(event) {
    event.stopPropagating();
    this.currentPagedImageIndex = event.first;
  }

  /**
   * search for profile in backend
   * @param event
   */
  searchContactUserProfile(event): void {
    this.searchProfiles(event.query).subscribe((result) => {
      this.contactProfilesSearch = result;
    });
  }

  get contactProfileIdField(): AbstractControl {
    return this.appointmentForm.get('contactId');
  }

  contactProfileSelected(event): void {
    this.contactProfileIdField.setValue(event.value.id);
  }

  contactProfileCleared(event): void {
    event.preventDefault();
    this.contactProfile = null;
    this.appointmentForm.patchValue({ contactId: null })
  }

  private searchProfiles(query: string): Observable<UserProfile[]> {
    return new Observable<UserProfile[]>((observer) => {
      const PROFILE_QUERY = gql`
    query GetProfiles($page: Int!,$size: Int!,$search: String,$sort:Sort!){
      findUserProfile(search : $search, page: { page : $page, size : $size, sort: $sort}) {
        totalcount
        content{
         id
         realname
        }}}`;

      this.apolloClient.query<Query>({
        query: PROFILE_QUERY,
        variables: {
          page: 0,
          size: 50,
          search: query,
          sort: { property: 'name', direction: Direction.Asc }
        }
      }).subscribe((result) => {
        if (!result.errors) {
          observer.next(result.data.findUserProfile.content);
          observer.complete();
        } else {
          observer.error(result.errors);
          observer.complete();
        }
      });
    });
  }


  /**
   * remove the group
   */
  removeContactGroup(): void {
    this.appointmentForm.get('contactGroupId').setValue(null);
    this.selectedGroup = null;
  }

  /**
   * opens picker
   * @param event
   */
  openContactGroupPicker(event): void {
    this.dialogService.open(GroupPickerComponent, {
      header: $localize`:@@appointment-management-edit.group-picker-dialog.title: Choose Group`,
      data: {
        selectionMode: 'single'
      },
      focusOnShow: false,
      modal: true,
      draggable: false,
      closable: true,
      closeAriaLabel:$localize`:@@appointment-management-edit.group-picker-dialog.closeButton:Close`,
      focusTrap: true,
      style: { 'min-height': '30rem', 'min-width': '25rem', display: 'flex' }
    }).onClose.subscribe((result) => {
      if (result) {
        this.appointmentForm.get('contactGroupId').setValue(result.id);
        this.selectedGroup = result;
      }
    });
  }


  /**
   * remove the group
   */
  onRemoveGroupRegistration(): void {
    this.appointmentForm.get('groupRegistration').setValue(null);
    this.selectedGroupRegistration = null;
  }

  /**
   * remove the group
   */
  onRemoveGroupInvitation(): void {
    this.appointmentForm.get('groupInvitation').setValue(null);
    this.selectedGroupInvitation = null;
  }
  /**
   * opens picker
   * @param event
   */
  onOpenGroupRegistrationPicker(event): void {
    this.dialogService.open(GroupPickerComponent, {
      header: $localize`:@@appointment-management-edit.group-picker-dialog.title: Choose group`,
      data: {
        selectionMode: 'single'
      },
      focusOnShow: false,
      modal: true,
      draggable: false,
      closable: true,
      closeAriaLabel:$localize`:@@appointment-management-edit.group-picker-dialog.closeButton:Close`,
      focusTrap: true,
      style: { 'min-height': '30rem', 'min-width': '25rem', display: 'flex' }
    }).onClose.subscribe((result) => {
      if (result) {
        this.appointmentForm.get('groupRegistration').setValue(result.id);
        this.selectedGroupRegistration = result;
      }
    });
  }

  /**
  * opens picker
  * @param event
  */
  onOpenGroupInvistationPicker(event): void {
    this.dialogService.open(GroupPickerComponent, {
      header: $localize`:@@appointment-management-edit.group-picker-dialog.title: Choose group`,
      data: {
        selectionMode: 'single'
      },
      focusOnShow: false,
      modal: true,
      draggable: false,
      closable: true,
      closeAriaLabel:$localize`:@@appointment-management-edit.group-picker-dialog.closeButton:Close`,
      focusTrap: true,
      style: { 'min-height': '30rem', 'min-width': '25rem', display: 'flex' }
    }).onClose.subscribe((result) => {
      if (result) {
        this.appointmentForm.get('groupInvitation').setValue(result.id);
        this.selectedGroupInvitation = result;
      }
    });
  }

  resetDeadline(): void {
    this.appointmentForm.get('deadlineStartOffsetMinutes').setValue(null);
    this.appointmentForm.get('deadline').setValue(null);
  }

  resetStartOfRegistration(): void {
    this.appointmentForm.get('startOfRegistrationOffsetMinutes').setValue(null);
    this.appointmentForm.get('startOfRegistration').setValue(null);

  }

  resetCancelationDeadline(): void {
    this.appointmentForm.get('cancelRegistrationDeadlineOffsetMinutes').setValue(null);
    this.appointmentForm.get('cancelRegistrationDeadline').setValue(null);
  }

  get startDateAsDate(): Date {
    const dateStart = this.appointmentForm.get('dateStart').value;
    const timeStart = this.appointmentForm.get('timeStart').value;
    if (dateStart && timeStart) {
      return moment(`${dateStart} ${timeStart}`, 'YYYY-MM-DD HH:mm').toDate();
    }
    if (dateStart) {
      return moment(`${dateStart}`, 'YYYY-MM-DD').toDate();
    }
    return null;
  }
  /**
   * new deadline selected
   */
  onDeadLineChanged(): void {
    const newDeadLine = this.appointmentForm.get('deadline').value;
    const startDate = this.startDateAsDate;

    if (newDeadLine == null || startDate == null) {
      this.appointmentForm.get('deadlineStartOffsetMinutes').setValue(null);
    } else {
      const minutes = moment(startDate).diff(newDeadLine, 'minutes');
      this.appointmentForm.get('deadlineStartOffsetMinutes').setValue(minutes);
    }
  }

  /**
   * new cancelation deadline selected
   */
  onCancelationDeadLineChanged(): void {
    const newDeadLine = this.appointmentForm.get('cancelRegistrationDeadline').value;
    const startDate = this.startDateAsDate;

    if (newDeadLine == null || startDate == null) {
      this.appointmentForm.get('cancelRegistrationDeadlineOffsetMinutes').setValue(null);
    } else {
      const minutes = moment(startDate).diff(newDeadLine, 'minutes');
      this.appointmentForm.get('cancelRegistrationDeadlineOffsetMinutes').setValue(minutes);
    }
  }

  /**
 * new deadline selected
 */
  onStartOfRegistrationChanged(): void {
    const newStartOfReistration = this.appointmentForm.get('startOfRegistration').value;
    const startDate = this.startDateAsDate;

    if (newStartOfReistration == null || startDate == null) {
      this.appointmentForm.get('startOfRegistrationOffsetMinutes').setValue(null);
    } else {
      const minutes = moment(startDate).diff(newStartOfReistration, 'minutes');
      this.appointmentForm.get('startOfRegistrationOffsetMinutes').setValue(minutes);
    }
  }


  /**
   * reset rrule on change
   */
  onCycleChanged(event): void {
    const value = event.value;
    if (value !== 'CUSTOM') {
      this.appointmentForm.get('rrule').setValue(null);
    }
  }

  /**
   * reset contactId on keyboard delete clicked
   */
    detectDeleteKeyPressed(event):void{
    var key = event.keyCode || event.charCode;

    // key number für backspace oder del
        if( key == 8 || key == 46 ){
      this.contactProfileCleared(event)
    }
  }

    hasProfile():boolean{
    return this.coreLibService.getSecurityInformation().userProfile != null;
  }

    getCategoryPlaceholder(isCalendarSelected: boolean): string{
        if(isCalendarSelected){
      return $localize`:@@appointment-management-edit.select-calender-first:Select a calendar first`;
    }
    return $localize`:@@appointment-management-edit.select-category:Select a category`;
  }

    configureTinyMce():void {
        this.tinyMceConfig= {
            base_url: this.baseHref + 'assets/tinymce',
            branding: false,
            height: 300,
            menubar: false,
            plugins: 'code lists fullscreen link emoticons table',
            forced_root_block : 'div',
            statusbar: false,
            paste_data_images: false,
            toolbar:
                'undo redo | bold italic underline | alignleft aligncenter alignRight | backcolor forecolor | blocks | bullist image link emoticons | table | removeformat fullscreen code',
            link_target_list : false,
        }

        // falls sprache nicht english oder fallback ist, muss localisation angepasst werden
        let tinyMceLanguage = "en";
        let languagePath = "";
        switch(this.language){
            case "de":
                tinyMceLanguage = "de";
                languagePath = tinyMceLanguage;
                break;
            case "fr":
                tinyMceLanguage = "fr"
                languagePath = "fr_FR";
                break;
            case "es":
                tinyMceLanguage = "es";
                languagePath = tinyMceLanguage;
                break;
            default :
                tinyMceLanguage = "en";
        }
        if(tinyMceLanguage != "en"){
            this.tinyMceConfig['language'] = tinyMceLanguage;
            this.tinyMceConfig['language_url'] = `${window.location.origin + this.baseHref}assets/tinyMce/langs/${languagePath}.js`;
        }
        this.editorLoaded = true;
    }

    hasLocationCreate(): boolean{
        return this.coreLibService.hasAnyClaims(['location_create']);
    }

    onModalShow(modal: string){
        let elem = document.getElementById(modal).querySelectorAll(".p-dialog-close-button").item(0) as HTMLElement;
        elem.focus();
    }

    createNewLocation(){
        this.dialogService.open(LocationEditComponent, {
            showHeader: false,
            focusOnShow: false,
            modal: true,
            width: '80%',
            closable: true,
            draggable: false,
            closeAriaLabel:$localize`:@@appointment-configuration.location.closeButton:Close`,
            focusTrap: true,
            data: { editedLocation: null}
        }).onClose.subscribe((location: any) => {
            if(location != null){
                this.messageService.add({ severity: 'success', detail: $localize`:@@appointment-configuration.location.added:Added location` } as ToastMessageOptions);
                this.allLocations = [...this.allLocations, location];

                this.appointmentForm.get('locationId').patchValue(location.id);
            }
        });
    }
}
